import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";

/**
 * BroadcastHistoryPage
 * - Overview stat cards
 * - Date range filter + search
 * - Table: Name, Template, Sent Date, Total Recipients, Delivered, Failed, Status
 */
export default function BroadcastHistoryPage() {
    const navigate = useNavigate();
    const [broadcasts, setBroadcasts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
    const ITEMS_PER_PAGE = 20;

    // Filters
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [appliedDateFrom, setAppliedDateFrom] = useState("");
    const [appliedDateTo, setAppliedDateTo] = useState("");
    const [searchText, setSearchText] = useState("");

    const fetchBroadcasts = async (page = 1) => {
        try {
            setError("");
            setLoading(true);
            // In a real server-side filter, we would pass dateFrom, dateTo, search also
            // For now, let's just paginate the primary list
            const res = await api.get(`/broadcasts?page=${page}&limit=${ITEMS_PER_PAGE}`);

            if (res.data && res.data.data) {
                setBroadcasts(res.data.data);
                setPagination({
                    total: res.data.total,
                    totalPages: res.data.totalPages
                });
                setCurrentPage(res.data.page);
            } else {
                setBroadcasts(res.data || []);
            }
        } catch {
            setError("Failed to load broadcasts");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBroadcasts(currentPage);
        const interval = setInterval(() => fetchBroadcasts(currentPage), 30000);
        return () => clearInterval(interval);
    }, [currentPage]);

    const applyDateFilter = () => {
        setAppliedDateFrom(dateFrom);
        setAppliedDateTo(dateTo);
        setCurrentPage(1); // Reset to first page
    };

    const clearFilters = () => {
        setDateFrom("");
        setDateTo("");
        setAppliedDateFrom("");
        setAppliedDateTo("");
        setSearchText("");
        setCurrentPage(1);
    };

    // Filtered broadcasts (Note: if we do server pagination, we should pass filters to API)
    const filtered = useMemo(() => {
        let list = [...broadcasts];

        // Search filter
        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            list = list.filter(
                (b) =>
                    (b.name || "").toLowerCase().includes(q) ||
                    (b.templateName || "").toLowerCase().includes(q)
            );
        }

        // Date range filter
        if (appliedDateFrom) {
            const from = new Date(appliedDateFrom);
            from.setHours(0, 0, 0, 0);
            list = list.filter((b) => new Date(b.createdAt) >= from);
        }
        if (appliedDateTo) {
            const to = new Date(appliedDateTo);
            to.setHours(23, 59, 59, 999);
            list = list.filter((b) => new Date(b.createdAt) <= to);
        }

        return list;
    }, [broadcasts, appliedDateFrom, appliedDateTo, searchText]);

    // Overview stats (from current page in this implementation)
    const stats = useMemo(() => {
        const s = { sent: 0, delivered: 0, failed: 0, processing: 0 };
        filtered.forEach((b) => {
            s.sent += b.sentCount || 0;
            s.delivered += b.deliveredCount || 0;
            s.failed += b.failedCount || 0;
            if (b.status === "running" || b.status === "pending") s.processing += 1;
        });
        return s;
    }, [filtered]);

    const statusClass = (status) => {
        switch (status) {
            case "completed":
                return "yc-bh-badge yc-bh-badge--completed";
            case "running":
            case "pending":
                return "yc-bh-badge yc-bh-badge--running";
            case "failed":
                return "yc-bh-badge yc-bh-badge--failed";
            case "paused":
                return "yc-bh-badge yc-bh-badge--paused";
            default:
                return "yc-bh-badge";
        }
    };

    return (
        <div className="yc-bh-page">
            {/* Header */}
            <div className="yc-bh-header">
                <h2 className="yc-bh-heading">Broadcast History</h2>
                <button
                    className="yc-bh-new-btn"
                    onClick={() => navigate("/broadcasts")}
                >
                    + New Broadcast
                </button>
            </div>

            {/* Date Range Filter */}
            <div className="yc-bh-filter-bar">
                <div className="yc-bh-filter-section">
                    <span className="yc-bh-filter-label">Date range filter</span>
                    <div className="yc-bh-date-group">
                        <div className="yc-bh-date-field">
                            <label>From</label>
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                            />
                        </div>
                        <div className="yc-bh-date-field">
                            <label>To</label>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                            />
                        </div>
                        <button className="yc-bh-apply-btn" onClick={applyDateFilter}>
                            Apply
                        </button>
                        {(appliedDateFrom || appliedDateTo || searchText) && (
                            <button className="yc-bh-clear-btn" onClick={clearFilters}>
                                Clear
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Overview Cards */}
            <div className="yc-bh-overview">
                <h3 className="yc-bh-overview-title">Overview</h3>
                <div className="yc-bh-cards">
                    <div className="yc-bh-card">
                        <span className="yc-bh-card-value">{stats.sent}</span>
                        <span className="yc-bh-card-icon yc-bh-card-icon--sent">✓</span>
                        <span className="yc-bh-card-label">Sent</span>
                    </div>
                    <div className="yc-bh-card">
                        <span className="yc-bh-card-value">{stats.delivered}</span>
                        <span className="yc-bh-card-icon yc-bh-card-icon--delivered">✓✓</span>
                        <span className="yc-bh-card-label">Delivered</span>
                    </div>
                    <div className="yc-bh-card">
                        <span className="yc-bh-card-value">{stats.failed}</span>
                        <span className="yc-bh-card-icon yc-bh-card-icon--failed">✕</span>
                        <span className="yc-bh-card-label">Failed</span>
                    </div>
                    <div className="yc-bh-card">
                        <span className="yc-bh-card-value">{stats.processing}</span>
                        <span className="yc-bh-card-icon yc-bh-card-icon--processing">⟳</span>
                        <span className="yc-bh-card-label">Processing</span>
                    </div>
                </div>
            </div>

            {/* Broadcast List */}
            <div className="yc-bh-list-section">
                <div className="yc-bh-list-toolbar">
                    <h3 className="yc-bh-list-title">Broadcast list</h3>
                    <div className="yc-bh-search-wrap">
                        <input
                            type="text"
                            className="yc-bh-search"
                            placeholder="Search by name or template..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                        <svg className="yc-bh-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" x2="16.65" y2="16.65" />
                        </svg>
                    </div>
                    <button className="yc-bh-refresh-btn" onClick={() => fetchBroadcasts(currentPage)}>
                        ↻ Refresh
                    </button>
                </div>

                {error && <div className="yc-bh-error">{error}</div>}

                {loading ? (
                    <div className="yc-bh-loading">Loading broadcasts...</div>
                ) : filtered.length === 0 ? (
                    <div className="yc-bh-empty">No broadcasts found.</div>
                ) : (
                    <>
                        <div className="yc-bh-table-wrap">
                            <table className="yc-bh-table">
                                <thead>
                                    <tr>
                                        <th>Broadcast Name</th>
                                        <th>Template</th>
                                        <th>Sent Date</th>
                                        <th>Total Recipients</th>
                                        <th>Delivered</th>
                                        <th>Failed</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((b) => {
                                        const id = b._id ?? b.id;
                                        return (
                                            <tr
                                                key={id}
                                                onClick={() => navigate(`/broadcasts?id=${id}`)}
                                            >
                                                <td className="yc-bh-name-cell">{b.name}</td>
                                                <td>{b.templateName || "—"}</td>
                                                <td>
                                                    {b.createdAt
                                                        ? new Date(b.createdAt).toLocaleString("en-US", {
                                                            year: "numeric",
                                                            month: "2-digit",
                                                            day: "2-digit",
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                            hour12: true,
                                                        })
                                                        : "—"}
                                                </td>
                                                <td>{b.totalRecipients ?? "—"}</td>
                                                <td>{b.deliveredCount ?? 0}</td>
                                                <td>{b.failedCount ?? 0}</td>
                                                <td>
                                                    <span className={statusClass(b.status)}>
                                                        {b.status || "unknown"}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Controls */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', padding: '10px' }}>
                            <div style={{ color: '#6b7280', fontSize: '14px' }}>
                                Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, pagination.total)} of {pagination.total} broadcasts
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    className="yc-bh-clear-btn"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(prev => prev - 1)}
                                    style={{ margin: 0, opacity: currentPage === 1 ? 0.5 : 1 }}
                                >
                                    Previous
                                </button>
                                {[...Array(pagination.totalPages)].map((_, i) => {
                                    const pageNum = i + 1;
                                    // Only show current, first, last, and neighbors
                                    if (
                                        pageNum === 1 ||
                                        pageNum === pagination.totalPages ||
                                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                    ) {
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #d1d5db',
                                                    backgroundColor: currentPage === pageNum ? '#9dc33b' : '#fff',
                                                    color: currentPage === pageNum ? '#fff' : '#374151',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    } else if (
                                        pageNum === currentPage - 2 ||
                                        pageNum === currentPage + 2
                                    ) {
                                        return <span key={pageNum}>...</span>;
                                    }
                                    return null;
                                })}
                                <button
                                    className="yc-bh-clear-btn"
                                    disabled={currentPage === pagination.totalPages}
                                    onClick={() => setCurrentPage(prev => prev + 1)}
                                    style={{ margin: 0, opacity: currentPage === pagination.totalPages ? 0.5 : 1 }}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
